import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import Axios from "axios";

import config from "../../config";
import { controller } from "../controller";
import MultiOfficeCheckout from "../../components/Subscription/MultiOfficeCheckout";
import { isUsableOfficeId } from "../../components/officeFeatures";
import "../../components/Subscription/checkout.css";

/**
 * Onboarding → choose your plans.
 *
 * The same checkout the app uses everywhere (MultiOfficeCheckout): a plan per
 * office, one payment. A card entry leaves the page for Helcim; the app-wide
 * useHelcimReturn finishes the purchase and reloads, and onboarding moves on
 * once the first charge is approved (usage.services.subscription_billing).
 *
 * Also here: the free trial (a request quota per office, no card) and Skip.
 */
const Purchase = (props) => {
  const [loading, setLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  const [trialConfig, setTrialConfig] = useState({ free_trials_enabled: false });
  const [officeId, setOfficeId] = useState(() => {
    const id = localStorage.getItem("selectedOffice");
    return isUsableOfficeId(id) ? String(id) : null;
  });

  // The offices this owner has made so far. The selected office is only
  // corrected when it isn't a real one — it used to be overwritten with the first
  // office every time, so a multi-office owner could buy for the wrong one.
  const getData = async () => {
    const response = await Axios.get(config.apiGateway.URL + "/clinics/selectoffice/", {
      headers: {
        Authorization: localStorage.getItem("user")
          ? "Token " + JSON.parse(localStorage.getItem("user")).key
          : "",
      },
    });
    const list = (response && response.data) || [];
    if (!list.length) return [];
    list.forEach((o) => { o.office_id = o.id; });
    localStorage.setItem("office_ids", JSON.stringify(list));
    const current = localStorage.getItem("selectedOffice");
    if (!isUsableOfficeId(current) || !list.some((o) => String(o.id) === String(current))) {
      localStorage.setItem("selectedOffice", String(list[0].id));
      localStorage.setItem("selectedOfficeName", list[0].office_name || "");
      setOfficeId(String(list[0].id));
    }
    setOffices(list);
    return list;
  };

  // The trial offer depends on the office (already trialing? used up?), so it is
  // asked for with the office id once getData() has settled it.
  const loadTrialConfig = async () => {
    try {
      const cfg = await controller.getTrialConfig(localStorage.getItem("selectedOffice") || undefined);
      if (cfg && cfg.status < 250) setTrialConfig(cfg);
    } catch (e) {
      // The trial button simply stays hidden.
    }
  };

  useEffect(() => {
    getData().catch(() => {}).then(loadTrialConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start the free trial for every office — each gets its own request quota.
  const handleStartTrial = async () => {
    setLoading(true);
    try {
      const ids = offices.length ? offices.map((o) => o.id) : [officeId];
      const results = await Promise.all(ids.map((id) => controller.startTrial(id).catch(() => null)));
      const ok = results.filter((r) => r && r.status < 250);
      if (ok.length) {
        message.success(
          ok.some((r) => r.result_status === "started")
            ? (ids.length > 1 ? "Free trials started for your offices" : "Your free trial has started")
            : "Continuing your free trial",
        );
        if (props.readOnboardingStatus) props.readOnboardingStatus();
        return;
      }
      const first = results.find(Boolean);
      const why = {
        already_used: "This office's free trial has already been used — pick a plan to continue.",
        has_subscription: "This office already has a subscription.",
        disabled: "Free trials aren't available for this account.",
      }[first && first.result_status];
      message.error(why || (first && first.detail) || "Couldn't start the free trial.");
    } catch (e) {
      message.error("Couldn't start the free trial.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    const res = await controller.skipOnBoarding();
    if (res.status < 250) await getData();
    window.location.reload();
  };

  const trial = trialConfig.trial || {};
  const offerTrial = trialConfig.trial ? !!trial.eligible : !!trialConfig.free_trials_enabled;
  const several = offices.length > 1;

  return (
    <div className="onboarding-plans">
      <p style={{ fontSize: 24, fontWeight: 600, marginBottom: 6 }}>
        {several ? "Choose your plans" : "Choose your plan"}
      </p>
      <p style={{ color: "#888", marginBottom: 20 }}>
        {several
          ? "Pick a plan for each of your offices — each covers that office's whole team. You'll pay once."
          : "Pick a subscription for your office — it covers your whole team."}
      </p>

      {officeId ? (
        <MultiOfficeCheckout
          officeId={officeId}
          onDone={() => (props.readOnboardingStatus ? props.readOnboardingStatus() : window.location.reload())}
        />
      ) : null}

      <div style={{ marginTop: 20, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
        {trial.exhausted ? (
          <div style={{ color: "#888", marginBottom: 12, textAlign: "center" }}>
            Your free trial has been used — choose a plan to continue.
          </div>
        ) : trial.active ? (
          <Button block loading={loading} onClick={handleStartTrial} style={{ marginBottom: 12 }}>
            Continue with your free trial
            {trial.remaining != null ? ` — ${trial.remaining} requests left` : ""}
          </Button>
        ) : offerTrial ? (
          <Button block loading={loading} onClick={handleStartTrial} style={{ marginBottom: 12 }}>
            Start free trial — {trialConfig.trial_request_quota || 25} free requests
            {several ? " per office" : ""}
          </Button>
        ) : null}
        <div className="skip-btn mt5" onClick={handleSkip}>
          Skip
        </div>
      </div>
    </div>
  );
};

export default Purchase;
